import React, {useEffect, useState} from 'react';
import {Transition} from "@headlessui/react";
import {IAccount, IAccountReq} from "../../model/IAccount";
import {accountApi} from "../../api/AccountsService";
import {categoryApi} from "../../api/CategoriesService";
import AccountField from "../ui/account/AccountField";
import AccountLogoPicker from "../ui/account/AccountLogoPicker";
import CurrencyPicker from "../ui/account/CurrencyPicker";
import AmountField from "../ui/transaction/AmountField";

interface EditAccountFormProps {
    account: IAccount;
    close: () => void;
}

const EditAccountForm: React.FunctionComponent<EditAccountFormProps> = ({account, close}) => {
    const {data: currencies} = categoryApi.useFetchAllCurrenciesQuery();
    const {data: accountLogos} = categoryApi.useFetchAllAccountLogosQuery();
    const [updateAccount, {isLoading}] = accountApi.useUpdateAccountMutation();

    const canChangeCurrency = !account.has_transactions;

    const [accountName, setAccountName] = useState(account.name);
    const [accountLogo, setAccountLogo] = useState(account.logo);
    const [currency, setCurrency] = useState(account.currency);
    const [initialAmount, setInitialAmount] = useState(account.initial_balance as any);

    useEffect(() => {
        setAccountName(account.name);
        setAccountLogo(account.logo);
        setCurrency(account.currency);
        setInitialAmount(account.initial_balance);
    }, [account]);

    function composeUpdatedAccount(): IAccountReq {
        return {
            name: accountName,
            logo: accountLogo,
            currency: currency.id,
            initial_balance: initialAmount,
        };
    }

    async function handleUpdateAccount() {
        const result = await updateAccount({
            id: account.id,
            account: composeUpdatedAccount(),
        });
        if ('data' in result) {
            close();
        } else {
            console.log(result.error);
        }
    }

    return (
        <div className="mb-1 relative py-1">
            <Transition.Child
                enter="ease-in-out duration-300 transform"
                enterFrom="-translate-y-full"
                enterTo="translate-y-0"
                leave="ease-in-out duration-300 transform"
                leaveFrom="translate-y-0"
                leaveTo="-translate-y-full"
                className="relative z-50 flex items-end justify-center flex-wrap mx-3 px-2"
            >
                {accountLogo && accountLogos && (
                    <AccountLogoPicker logo={accountLogo} setLogo={setAccountLogo} logos={accountLogos} smallerSize/>
                )}
                <AccountField label="Account Name" accountName={accountName} setAccountName={setAccountName} smallerSize/>
                {canChangeCurrency && currency && currencies && (
                    <CurrencyPicker label="Currency" currency={currency} setCurrency={setCurrency} currencies={currencies} smallerSize/>
                )}
                {!canChangeCurrency && currency && (
                    <div className="mt-2 mx-3">
                        <label className="ml-1 block text-xs font-medium text-gray-700">Currency</label>
                        <div className="mt-0.5 h-7 flex items-center px-2 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-md">
                            {currency.code} — {currency.name}
                        </div>
                    </div>
                )}
                {currency && (
                    <AmountField
                        label="Initial Balance"
                        currency={currency}
                        amount={initialAmount}
                        setAmount={setInitialAmount}
                        smallerSize
                        className="mt-2 mx-3"
                    />
                )}
                <div className="flex items-center ml-3 mb-0.5 gap-2">
                    <button
                        className="group relative flex justify-center py-1 px-3 border border-transparent
                                   text-xs font-medium rounded-md bg-gray-200 text-red-500 hover:text-red-800"
                        onClick={close}
                    >
                        Cancel
                    </button>
                    <button
                        className="group relative flex justify-center py-1 px-3 border border-transparent
                                   text-xs font-medium rounded-md bg-green-100 text-green-800 hover:bg-green-200 focus:outline-none"
                        disabled={isLoading}
                        onClick={handleUpdateAccount}
                    >
                        Save
                    </button>
                </div>
            </Transition.Child>
        </div>
    );
};

export default EditAccountForm;
